import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Settings as SettingsIcon, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const [settings, setSettings] = useState({
    filing_status: "single",
    state: "CA",
    dependents: 0,
    self_employed: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tax-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/tax-settings`, settings);
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-4xl sm:text-5xl font-heading font-light tracking-tight" data-testid="settings-title">
          Tax Settings
        </h1>
        <p className="text-lg text-muted-foreground mt-2">Configure your tax profile for accurate calculations</p>
      </motion.div>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <SettingsIcon className="w-5 h-5" />
            <CardTitle className="font-heading font-light">Your Tax Profile</CardTitle>
          </div>
          <CardDescription>Update your information to get the most accurate tax estimates</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveSettings} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="filing-status">Filing Status</Label>
                <Select
                  value={settings.filing_status}
                  onValueChange={(value) => setSettings({ ...settings, filing_status: value })}
                >
                  <SelectTrigger id="filing-status" data-testid="settings-filing-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married">Married Filing Jointly</SelectItem>
                    <SelectItem value="married_separate">Married Filing Separately</SelectItem>
                    <SelectItem value="head">Head of Household</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="state">State</Label>
                <Select value={settings.state} onValueChange={(value) => setSettings({ ...settings, state: value })}>
                  <SelectTrigger id="state" data-testid="settings-state">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CA">California</SelectItem>
                    <SelectItem value="NY">New York</SelectItem>
                    <SelectItem value="TX">Texas (No State Tax)</SelectItem>
                    <SelectItem value="FL">Florida (No State Tax)</SelectItem>
                    <SelectItem value="WA">Washington (No State Tax)</SelectItem>
                    <SelectItem value="IL">Illinois</SelectItem>
                    <SelectItem value="PA">Pennsylvania</SelectItem>
                    <SelectItem value="OH">Ohio</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="dependents">Number of Dependents</Label>
                <Input
                  id="dependents"
                  type="number"
                  min="0"
                  value={settings.dependents}
                  onChange={(e) => setSettings({ ...settings, dependents: parseInt(e.target.value) || 0 })}
                  data-testid="settings-dependents"
                />
              </div>

              <div className="flex items-center space-x-4 pt-8">
                <Switch
                  id="self-employed"
                  checked={settings.self_employed}
                  onCheckedChange={(checked) => setSettings({ ...settings, self_employed: checked })}
                  data-testid="settings-self-employed"
                />
                <Label htmlFor="self-employed" className="cursor-pointer">
                  Self-employed / Freelancer
                </Label>
              </div>
            </div>

            <div className="pt-6 border-t border-border">
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="save-settings-button">
                {saving ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </span>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-accent/20 bg-accent/5">
        <CardHeader>
          <CardTitle className="text-lg font-heading font-light">About TaxFlow AI</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            TaxFlow AI helps you track income, expenses, and deductions throughout the year. Our AI-powered features
            provide smart categorization and tax advice to maximize your savings.
          </p>
          <div className="flex items-center space-x-2 text-sm">
            <span className="px-2 py-1 bg-primary/10 text-primary rounded-full">Powered by GPT-5.2</span>
            <span className="px-2 py-1 bg-secondary text-secondary-foreground rounded-full">Version 1.0</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
